import {
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  CircularProgress,
} from '@mui/material';
import { Header } from 'components/home';
import { ContainerForm } from 'components/shared/container_form';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { CuffaAPI } from 'api/cuffa/cuffa.api';
import { ROUTES } from 'routes/constants';

const instituicoesPadrao = [
  { id: 'empaer', name: 'Empaer' },
  { id: 'embrapa', name: 'Embrapa' },
  { id: 'ifmt', name: 'IFMT' },
  { id: 'ongs', name: 'ONGs' },
  { id: 'senar', name: 'Senar' },
  { id: 'universidades_publicas', name: 'Universidades públicas' },
  { id: 'cooperativas', name: 'Cooperativas' },
  { id: 'empresas_privadas', name: 'Empresas privadas' },
  { id: 'incra', name: 'Incra' },
  { id: 'sebrae', name: 'Sebrae' },
  { id: 'universidades_privadas', name: 'Universidades privadas' },
];

interface IVisitasInstituicoesData {
  recebeuVisita: string;
  instituicoesSelecionadas: string[];
  instituicoesAdicionais: string[];
}

export const VisitasInstituicoesVisualizar: React.FC = () => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();

  // 2. Estado de 'loading' adicionado e 'useCuffaStore' removido
  const [dados, setDados] = useState<IVisitasInstituicoesData | null>(null);
  const [loading, setLoading] = useState(true);

  // 3. useEffect foi reescrito para buscar dados diretamente da API
  useEffect(() => {
    const fetchVisitsData = async () => {
      if (!uuid) {
        setLoading(false);
        return;
      }
      // Tenta restaurar do localStorage 'cuffa-storage' antes de chamar a API
      try {
        const storageKey = 'cuffa-storage';
        const raw = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            const saved = parsed?.state?.dadosParciais?.visitasInstituicoes;
            if (saved) {
              const mappedData: IVisitasInstituicoesData = {
                recebeuVisita: saved.recebeuVisita ?? (typeof saved.received_institutional_visits === 'boolean' ? (saved.received_institutional_visits ? 'sim' : 'não') : (saved.recebeuVisitas ?? 'não')),
                instituicoesSelecionadas: saved.instituicoesSelecionadas || saved.selected_institutions_ids || [],
                instituicoesAdicionais: saved.instituicoesAdicionais || saved.other_institutions_list || [],
              };
              setDados(mappedData);
              setLoading(false);
              return;
            }
          } catch (e) {
            console.warn('Falha ao parsear cuffa-storage.visitasInstituicoes', e);
          }
        }
      } catch (e) {
        console.warn('Erro ao acessar localStorage cuffa-storage', e);
      }

      // Fallback: buscar na API
      try {
        const response = await CuffaAPI.getDataByID(uuid);

        if (response && response.dados) {
          const apiData = response.dados;
          const mappedData: IVisitasInstituicoesData = {
            recebeuVisita: apiData.received_institutional_visits ? 'sim' : 'não',
            instituicoesSelecionadas: apiData.selected_institutions_ids || [],
            instituicoesAdicionais: apiData.other_institutions_list || [],
          };
          setDados(mappedData);
        } else {
          console.error("Dados de visitas não encontrados na resposta da API:", response);
          setDados(null);
        }
      } catch (error) {
        console.error("Erro ao buscar dados de visitas:", error);
        setDados(null);
      } finally {
        setLoading(false);
      }
    };

    fetchVisitsData();
  }, [uuid]);

  // 4. Adicionados blocos para exibir o status de 'Carregando' e 'Erro'
  if (loading) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Visitas de Instituições" showBackButton />
        <Box width="100%" display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
        </Box>
      </MenuProvider>
    );
  }

  if (!dados) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Visitas de Instituições" showBackButton />
        <Box width="100%" display="flex" justifyContent="center" margin="2rem 0">
          <Typography>Não foi possível carregar os dados. Verifique o console para mais detalhes.</Typography>
        </Box>
      </MenuProvider>
    );
  }

  const instituicoesSelecionadasNomes = dados.instituicoesSelecionadas.map(id =>
    instituicoesPadrao.find(inst => inst.id === id)?.name || id
  );

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title="Visualização das Visitas de Instituições" showBackButton />
      <Box width="100%" display="flex" justifyContent="center" margin="2rem">
        <Box sx={{ maxWidth: '1152px', padding: '2rem', width: '100%' }}>
          <ContainerForm title="Visitas de Instituições" width="100%">
            {/* Seção: Recebeu visita? */}
            <Paper elevation={0} sx={{ p: 3, mb: 4, border: '2px solid #000', borderRadius: '8px' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                A unidade familiar recebeu a visita de alguma instituição prestadora de assistência, exceto a prefeitura, no último ano?
              </Typography>
              <Typography variant="body1" gutterBottom>
                {dados.recebeuVisita === 'sim' ? 'Sim' : 'Não'}
              </Typography>
            </Paper>

            {/* Seção: Instituições visitantes (se aplicável) */}
            {dados.recebeuVisita === 'sim' && (
              <Paper elevation={0} sx={{ p: 3, mb: 4, border: '2px solid #000', borderRadius: '8px' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Instituições que visitaram a unidade familiar
                </Typography>

                {/* Instituições padrão selecionadas */}
                {instituicoesSelecionadasNomes.length > 0 && (
                  <Box mb={4}>
                    <Typography variant="subtitle1" gutterBottom>
                      Instituições selecionadas:
                    </Typography>
                    <Grid container spacing={1}>
                      {instituicoesSelecionadasNomes.map((nome, index) => (
                        <Grid item key={index}>
                          <Chip label={nome} color="primary" variant="outlined" />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Instituições adicionais */}
                {dados.instituicoesAdicionais.length > 0 && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      Outras instituições:
                    </Typography>
                    <List dense>
                      {dados.instituicoesAdicionais.map((instituicao, index) => (
                        <div key={index}>
                          <ListItem>
                            <ListItemText primary={instituicao} />
                          </ListItem>
                          {index < dados.instituicoesAdicionais.length - 1 && <Divider />}
                        </div>
                      ))}
                    </List>
                  </Box>
                )}

                {instituicoesSelecionadasNomes.length === 0 && dados.instituicoesAdicionais.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma instituição registrada
                  </Typography>
                )}
              </Paper>
            )}

            <Box marginTop="2rem" display="flex" justifyContent="center" gap="1rem">
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate(ROUTES.IDENTIFICACAO_FAMILIAR_CUFFA_VISUALIZAR.replace(':uuid', uuid || ''))}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  if (uuid) {
                    navigate(ROUTES.CADASTRAR_PRODUTOS_VISUALIZAR.replace(':uuid', uuid));
                  }
                }}
              >
                Avançar
              </Button>
            </Box>
          </ContainerForm>
        </Box>
      </Box>
    </MenuProvider>
  );
};

export default VisitasInstituicoesVisualizar;
